import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Download, FileText, TrendingUp } from 'lucide-react';

function ReportsPanel() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [token, setToken] = useState('demo_token_123'); // Token em memória
  
  const loadReport = async () => {
    setLoading(true);
    
    try {
      // Simulação de dados para demonstração
      const mockData = {
        metrics: {
          totalCampaigns: 24,
          totalBudget: '45.280',
          averagePerformance: {
            ctr: 3.2
          },
          optimizationsApplied: 18,
          platformDistribution: {
            meta: 12,
            google: 8,
            linkedin: 4
          }
        },
        chartData: {
          budgetByPlatform: [
            { platform: 'Meta Ads', budget: 18500 },
            { platform: 'Google Ads', budget: 15800 },
            { platform: 'LinkedIn', budget: 10980 }
          ]
        }
      };
      
      setTimeout(() => {
        setReportData(mockData);
        setLoading(false);
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadReport();
  }, [period]);
  
  const exportPDF = () => {
    alert(`Exportando relatório em PDF do período: ${period}`);
    // Em produção, implementar a lógica real de exportação
  };
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
  
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <TrendingUp className="w-8 h-8" />
          Relatórios de Performance
        </h1>
        
        <div className="flex gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">Última Semana</option>
            <option value="month">Último Mês</option>
            <option value="quarter">Último Trimestre</option>
          </select>
          
          <button
            onClick={exportPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Download className="w-4 h-4" />
            Exportar PDF
          </button>
        </div>
      </div>
      
      {reportData && (
        <>
          {/* Cards de Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-600 text-sm mb-2">Total de Campanhas</p>
              <p className="text-4xl font-bold text-gray-800">{reportData.metrics.totalCampaigns}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-600 text-sm mb-2">Orçamento Total</p>
              <p className="text-4xl font-bold text-gray-800">R$ {reportData.metrics.totalBudget}</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-600 text-sm mb-2">CTR Médio</p>
              <p className="text-4xl font-bold text-gray-800">{reportData.metrics.averagePerformance.ctr}%</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <p className="text-gray-600 text-sm mb-2">Otimizações</p>
              <p className="text-4xl font-bold text-gray-800">{reportData.metrics.optimizationsApplied}</p>
            </div>
          </div>
          
          {/* Gráficos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Orçamento por Plataforma</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reportData.chartData.budgetByPlatform}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="platform" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="budget" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Distribuição de Campanhas</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Meta Ads', value: reportData.metrics.platformDistribution.meta },
                      { name: 'Google Ads', value: reportData.metrics.platformDistribution.google },
                      { name: 'LinkedIn', value: reportData.metrics.platformDistribution.linkedin }
                    ]}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ReportsPanel;